import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Conge, StatutConge, TypeConge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';
import { CongesService } from '@domain/rh/CongesService';
import { loadStcConfig, type StcCongesConfig } from '@application/rh/StcConfig';
import {
  creerDemandeConge,
  listConges,
  transiterConge,
} from '@application/rh/CongesDirectoryService';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { esc, fmtDate } from '@presentation/shell/format';
import { ToastService } from '@presentation/components/Toast';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { FormHelper } from '@presentation/components/FormHelper';
import { openFormModal } from '@presentation/shell/openFormModal';
import { renderRowActions } from '@presentation/shell/rowActions';
import { renderCongeForm } from '@presentation/views/forms/CongeFormView';
import { CongeDemandeSchema } from '@shared/validation/CongeSchema';
import { onExclusive } from '@presentation/shell/viewDom';

const TYPE_LABEL: Record<TypeConge, string> = {
  annuel: 'Annuel',
  maternite: 'Maternité',
  paternite: 'Paternité',
  maladie: 'Maladie',
  evenement_familial: 'Événement familial',
  sans_solde: 'Sans solde',
  autre: 'Autre',
};

const STATUT_LABEL: Record<StatutConge, string> = {
  demande: 'En attente',
  valide: 'Approuvé',
  refuse: 'Refusé',
  pris: 'Pris',
  annule: 'Annulé',
};

interface CongesViewState {
  tenantId: string;
  canMutate: boolean;
  salaries: Salarie[];
  eligibles: Salarie[];
  conges: Conge[];
  congesConfig?: StcCongesConfig;
}

function tenantIdOf(ctx: { tenantContext: { tryGetTenant: () => { id: string } | null } }): string {
  return ctx.tenantContext.tryGetTenant()?.id ?? 'default';
}

function nomSalarie(salaries: readonly Salarie[], id: string): string {
  const sal = salaries.find((s) => s.id === id);
  return sal ? `${sal.prenom} ${sal.nom}` : id;
}

function renderDemandes(section: HTMLElement, state: CongesViewState): void {
  const q = (section.querySelector<HTMLInputElement>('#search-conges')?.value ?? '')
    .toLowerCase()
    .trim();
  const statut = section.querySelector<HTMLSelectElement>('#filter-conges-statut')?.value ?? '';
  const type = section.querySelector<HTMLSelectElement>('#filter-conges-type')?.value ?? '';
  const empty = section.querySelector<HTMLElement>('#conges-empty');
  const tbody = section.querySelector<HTMLElement>('#conges-tbody');
  let list = state.conges.slice().sort((a, b) => b.cree_le.localeCompare(a.cree_le));
  if (statut) list = list.filter((c) => c.statut === statut);
  if (type) list = list.filter((c) => c.type === type);
  if (q) {
    list = list.filter((c) => {
      const nom = nomSalarie(state.salaries, c.salarie_id);
      return `${nom} ${TYPE_LABEL[c.type]} ${STATUT_LABEL[c.statut]} ${c.motif ?? ''}`
        .toLowerCase()
        .includes(q);
    });
  }
  if (empty) empty.style.display = list.length === 0 ? '' : 'none';
  if (!tbody) return;
  tbody.innerHTML = list
    .map((c) => {
      const nom = nomSalarie(state.salaries, c.salarie_id);
      const actions = state.canMutate
        ? renderRowActions(
            c.id,
            [
              ...(c.statut === 'demande'
                ? [
                    { action: 'approve', label: 'Approuver' },
                    { action: 'refuse', label: 'Refuser', variant: 'danger' as const },
                  ]
                : []),
              ...(c.statut === 'valide' ? [{ action: 'taken', label: 'Marquer pris' }] : []),
              ...(c.statut === 'demande' || c.statut === 'valide'
                ? [{ action: 'cancel', label: 'Annuler', variant: 'ghost' as const }]
                : []),
            ].map((a) => ({ ...a, adminOnly: true })),
          )
        : '';
      return `<tr>
          <td><strong>${esc(nom)}</strong></td>
          <td>${esc(TYPE_LABEL[c.type])}</td>
          <td>${esc(fmtDate(c.date_debut))}</td>
          <td>${esc(fmtDate(c.date_fin))}</td>
          <td class="num">${c.jours}</td>
          <td>${esc(STATUT_LABEL[c.statut])}</td>
          <td class="num">${actions}</td>
        </tr>`;
    })
    .join('');
}

function renderSoldes(section: HTMLElement, state: CongesViewState): void {
  const q = (section.querySelector<HTMLInputElement>('#search-soldes')?.value ?? '')
    .toLowerCase()
    .trim();
  const filtre = section.querySelector<HTMLSelectElement>('#filter-soldes')?.value ?? '';
  const soldes = section.querySelector<HTMLElement>('#soldes-tbody');
  if (!soldes) return;
  let list = state.eligibles;
  if (q) {
    list = list.filter((s) =>
      `${s.prenom} ${s.nom} ${s.matricule} ${s.poste ?? ''}`.toLowerCase().includes(q),
    );
  }
  if (filtre === 'positif') {
    list = list.filter((s) => CongesService.calculerSolde(s, state.conges, new Date(), state.congesConfig) > 0);
  } else if (filtre === 'zero') {
    list = list.filter((s) => CongesService.calculerSolde(s, state.conges, new Date(), state.congesConfig) <= 0);
  }
  soldes.innerHTML =
    list.length === 0
      ? '<tr><td colspan="6" style="color:var(--gris);padding:14px;">Aucun salarié ne correspond aux filtres</td></tr>'
      : list
          .map((s) => {
            const acquis = CongesService.calculerDroitsAcquis(s, new Date(), state.congesConfig);
            const pris = CongesService.joursPris(s, state.conges);
            const enCours = CongesService.joursEnCours(s, state.conges);
            const solde = CongesService.calculerSolde(s, state.conges, new Date(), state.congesConfig);
            const fige = typeof s.conges_solde === 'number';
            const count24 = state.conges.filter((c) => {
              if (c.salarie_id !== s.id) return false;
              const cutoff = new Date();
              cutoff.setMonth(cutoff.getMonth() - 24);
              const ref = new Date(`${c.date_debut}T00:00:00`);
              return !Number.isNaN(ref.getTime()) && ref >= cutoff;
            }).length;
            return `<tr>
            <td><strong>${esc(s.prenom)} ${esc(s.nom)}</strong><br><span class="mono" style="font-size:11px;color:var(--gris);">${esc(s.matricule)}${s.poste ? ` · ${esc(s.poste)}` : ''}</span></td>
            <td class="num">${acquis}</td>
            <td class="num">${pris}</td>
            <td class="num">${enCours}</td>
            <td class="num" style="font-weight:700;color:var(--teal);">${solde}${fige ? '<br><small style="font-weight:400;color:var(--gris);">solde figé (sortie)</small>' : ''}</td>
            <td class="num"><button class="btn btn-ghost btn-sm" data-action="history24" data-id="${esc(s.id)}">Voir (${count24})</button></td>
          </tr>`;
          })
          .join('');
}

export const congesController: ViewController = async (section, ctx, shell) => {
  const tenantId = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const allSalaries = await ctx.salaries.list();
  const eligibles = allSalaries.filter(
    (s) => s.statut === 'actif' || s.statut === 'suspendu' || s.statut === 'conge',
  );
  const [conges, stc] = await Promise.all([listConges(tenantId), loadStcConfig(tenantId)]);
  const pending = conges.filter((c) => c.statut === 'demande').length;
  const state: CongesViewState = {
    tenantId,
    canMutate,
    salaries: allSalaries,
    eligibles,
    conges,
    congesConfig: stc.conges,
  };
  (section as HTMLElement & { __nxConges?: CongesViewState }).__nxConges = state;

  const btnNew = section.querySelector<HTMLElement>('#btn-new-conge');
  if (btnNew) btnNew.style.display = canMutate ? '' : 'none';

  const badge = section.querySelector('#badge-pending');
  if (badge) badge.textContent = String(pending);

  renderDemandes(section, state);
  renderSoldes(section, state);

  const openCreate = (): void => {
    if (!canMutate) return;
    if (eligibles.length === 0) {
      ToastService.error('Aucun salarié actif');
      return;
    }
    openFormModal(section, { title: 'Nouvelle demande de congé', size: 'md' }, (host, close) => {
      host.innerHTML = renderCongeForm({ salaries: eligibles });
      host.querySelector('[data-cancel]')?.addEventListener('click', close);
      const form = host.querySelector<HTMLFormElement>('#conge-form')!;
      FormHelper.bindSubmit(form, CongeDemandeSchema, async (data) => {
        const salarie = eligibles.find((s) => s.id === data.salarie_id);
        if (!salarie) throw new Error('Salarié introuvable');
        await creerDemandeConge(tenantId, salarie, {
          type: data.type,
          date_debut: data.date_debut,
          date_fin: data.date_fin,
          motif: data.motif,
          statut: data.statut,
        });
        ToastService.success('Demande enregistrée');
        close();
        await congesController(section, ctx, shell);
      });
    });
  };

  const w = window as unknown as Record<string, unknown>;
  w['openCongeModal'] = openCreate;

  const rerender = (): void => {
    const latest = (section as HTMLElement & { __nxConges?: CongesViewState }).__nxConges;
    if (!latest) return;
    renderDemandes(section, latest);
    renderSoldes(section, latest);
  };
  onExclusive(section.querySelector('#search-conges'), 'input', rerender);
  onExclusive(section.querySelector('#filter-conges-statut'), 'change', rerender);
  onExclusive(section.querySelector('#filter-conges-type'), 'change', rerender);
  onExclusive(section.querySelector('#search-soldes'), 'input', rerender);
  onExclusive(section.querySelector('#filter-soldes'), 'change', rerender);

  onExclusive(section, 'click', (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-action]');
    if (!btn) return;
    if (btn.dataset.action === 'history24') {
      const salarieId = btn.dataset.id;
      const salarie = state.salaries.find((s) => s.id === salarieId);
      if (!salarie) return;
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - 24);
      const historiques = state.conges
        .filter((c) => c.salarie_id === salarie.id)
        .filter((c) => {
          const ref = new Date(`${c.date_debut}T00:00:00`);
          return !Number.isNaN(ref.getTime()) && ref >= cutoff;
        })
        .sort((a, b) => b.date_debut.localeCompare(a.date_debut));

      openFormModal(
        section,
        { title: `Historique congés (24 mois) — ${salarie.prenom} ${salarie.nom}`, size: 'lg' },
        (host, close) => {
          host.innerHTML = `<div style="max-height:56vh;overflow:auto;">
            <table class="data">
              <thead><tr><th>Type</th><th>Du</th><th>Au</th><th class="num">Jours</th><th>Statut</th><th>Motif</th></tr></thead>
              <tbody>
                ${
                  historiques.length === 0
                    ? '<tr><td colspan="6" style="color:var(--gris);padding:14px;">Aucun congé sur les 24 derniers mois.</td></tr>'
                    : historiques
                        .map(
                          (c) => `<tr>
                      <td>${esc(TYPE_LABEL[c.type])}</td>
                      <td>${esc(fmtDate(c.date_debut))}</td>
                      <td>${esc(fmtDate(c.date_fin))}</td>
                      <td class="num">${c.jours}</td>
                      <td>${esc(STATUT_LABEL[c.statut])}</td>
                      <td>${esc(c.motif ?? '—')}</td>
                    </tr>`,
                        )
                        .join('')
                }
              </tbody>
            </table>
          </div>
          <div style="display:flex;justify-content:flex-end;margin-top:12px;"><button class="btn btn-secondary" data-close-history>Fermer</button></div>`;
          host.querySelector('[data-close-history]')?.addEventListener('click', close);
        },
      );
      return;
    }

    if (!canMutateRecords(shell.permission)) return;
    const nextByAction: Record<string, StatutConge> = {
      approve: 'valide',
      refuse: 'refuse',
      taken: 'pris',
      cancel: 'annule',
    };
    const next = nextByAction[btn.dataset.action ?? ''];
    const congeId = btn.dataset.id;
    if (!next || !congeId) return;
    void (async () => {
      const labels: Record<StatutConge, string> = {
        valide: 'Approuver cette demande ?',
        refuse: 'Refuser cette demande ?',
        pris: 'Marquer ce congé comme pris ?',
        annule: 'Annuler cette demande ?',
        demande: '',
      };
      const ok = await confirmAction({
        host: section,
        title: STATUT_LABEL[next],
        message: labels[next],
        confirmLabel: STATUT_LABEL[next],
        danger: next === 'refuse' || next === 'annule',
      });
      if (!ok) return;
      try {
        await transiterConge(tenantIdOf(ctx), congeId, next, ctx.auth.getSession()?.user_id);
        ToastService.success('Demande mise à jour');
        await congesController(section, ctx, shell);
      } catch (error) {
        ToastService.error(error instanceof Error ? error.message : 'Action impossible');
      }
    })();
  });
};
